import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared } from '@krp/client-runtime'

import { dropResource } from '../../../shared/model/keys'
import type { CashierChoice } from './types'

/**
 * Выбор человека в «Начале работы» (RP-FB22 5): кассир, вид цены, касса. Это намерение, а не данные сервера:
 * на сервере до «Открыть смену» ничего не создаётся, поэтому «назад» и F5 ничего не оставляют и ничего не теряют.
 * Срок — `device`. Код кассира здесь не хранится никогда — только выбор.
 */
export interface StartDraft {
  /** Кассир, выбранный в этом заходе; `null` — выбора не было, действует «как в прошлый раз». */
  cashier: CashierChoice | null
  /** Вид цены (`cash` или `<источник>/<ключ>`), выбранный в этом заходе. */
  priceTypeId: string | null
  deviceId: number | null
}

/** С чем в прошлый раз начали работу на этом устройстве: из этого берутся значения по умолчанию. */
export interface StartLast {
  cashier: CashierChoice | null
  deviceId: number | null
  /** Вид цены по кассиру: ключ — id кассира (`''` — под своим именем). */
  priceTypes: Record<string, string>
}

export interface StartMemory {
  draft: StartDraft
  last: StartLast
}

export interface StartIntentState {
  /** Ключ — rid источника в нижнем регистре. */
  byResource: Record<string, StartMemory>
}

const initialState: StartIntentState = { byResource: {} }

const emptyMemory = (): StartMemory => ({
  draft: { cashier: null, priceTypeId: null, deviceId: null },
  last: { cashier: null, deviceId: null, priceTypes: {} },
})

const keyOf = (resourceId: string) => resourceId.toLowerCase()

/** Ключ вида цены по кассиру: у работы под своим именем он пустой. */
export const cashierKeyOf = (cashier: CashierChoice | null): string => (cashier?.kind === 'cashier' ? cashier.id.toLowerCase() : '')

function memoryOf(state: StartIntentState, resourceId: string): StartMemory {
  const key = keyOf(resourceId)
  state.byResource[key] ??= emptyMemory()
  return state.byResource[key]
}

const startSlice = createSlice({
  name: 'aqsi/start',
  initialState,
  reducers: {
    cashierChosen(state, action: PayloadAction<{ resourceId: string; cashier: CashierChoice }>) {
      const memory = memoryOf(state, action.payload.resourceId)
      memory.draft.cashier = action.payload.cashier
      // Вид цены по умолчанию свой у каждого кассира: прежний выбор относился к другому.
      memory.draft.priceTypeId = null
    },
    startPriceTypeChosen(state, action: PayloadAction<{ resourceId: string; priceTypeId: string }>) {
      memoryOf(state, action.payload.resourceId).draft.priceTypeId = action.payload.priceTypeId
    },
    startRegisterChosen(state, action: PayloadAction<{ resourceId: string; deviceId: number }>) {
      memoryOf(state, action.payload.resourceId).draft.deviceId = action.payload.deviceId
    },
    /** Работа за кассой начата: выбор этого захода стал «как в прошлый раз», черновик пуст. */
    startWorkRemembered(
      state,
      action: PayloadAction<{ resourceId: string; cashier: CashierChoice | null; deviceId: number; priceTypeId: string }>,
    ) {
      const memory = memoryOf(state, action.payload.resourceId)
      const { cashier, deviceId, priceTypeId } = action.payload
      memory.last.cashier = cashier
      memory.last.deviceId = deviceId
      memory.last.priceTypes[cashierKeyOf(cashier)] = priceTypeId
      memory.draft = { cashier: null, priceTypeId: null, deviceId: null }
    },
    startMemoryRestored(state, action: PayloadAction<Record<string, StartMemory>>) {
      state.byResource = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
    builder.addCase(resourceScopeCleared, (state, action) => dropResource(state.byResource, action.payload.resourceId))
  },
})

export const { cashierChosen, startPriceTypeChosen, startRegisterChosen, startWorkRemembered, startMemoryRestored } =
  startSlice.actions
export const startReducer = startSlice.reducer

const NO_MEMORY: StartMemory = emptyMemory()

/** Память «Начала работы» источника; нет записи — пустая (постоянный объект, чтобы селекторы не пересобирались). */
export function startMemoryOf(state: StartIntentState, resourceId: string): StartMemory {
  return state.byResource[keyOf(resourceId)] ?? NO_MEMORY
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function decodeChoice(raw: unknown): CashierChoice | null | undefined {
  if (raw === null) return null
  if (!isObject(raw)) return undefined
  if (raw.kind === 'own') return { kind: 'own' }
  if (raw.kind === 'cashier' && typeof raw.id === 'string' && raw.id !== '' && typeof raw.name === 'string') {
    return { kind: 'cashier', id: raw.id, name: raw.name.slice(0, 200) }
  }
  return undefined
}

const optionalDevice = (raw: unknown): number | null | undefined =>
  raw === null ? null : typeof raw === 'number' && Number.isInteger(raw) ? raw : undefined

function decodeMemory(raw: unknown): StartMemory | undefined {
  if (!isObject(raw) || !isObject(raw.draft) || !isObject(raw.last)) return undefined
  const draftCashier = decodeChoice(raw.draft.cashier)
  const draftDevice = optionalDevice(raw.draft.deviceId)
  const lastCashier = decodeChoice(raw.last.cashier)
  const lastDevice = optionalDevice(raw.last.deviceId)
  const draftPrice = raw.draft.priceTypeId
  if (draftCashier === undefined || draftDevice === undefined || lastCashier === undefined || lastDevice === undefined) return undefined
  if (draftPrice !== null && typeof draftPrice !== 'string') return undefined
  if (!isObject(raw.last.priceTypes)) return undefined
  const priceTypes: Record<string, string> = {}
  for (const [key, value] of Object.entries(raw.last.priceTypes)) {
    if (typeof value !== 'string') return undefined
    priceTypes[key] = value
  }
  return {
    draft: { cashier: draftCashier, priceTypeId: draftPrice, deviceId: draftDevice },
    last: { cashier: lastCashier, deviceId: lastDevice, priceTypes },
  }
}

/** Проверка прочитанного из хранилища (D-072): повреждённая запись — `undefined`, слайс остаётся пустым. */
export function decodeStartMemories(raw: unknown): Record<string, StartMemory> | undefined {
  if (!isObject(raw)) return undefined
  const result: Record<string, StartMemory> = {}
  for (const [key, value] of Object.entries(raw)) {
    const memory = decodeMemory(value)
    if (memory === undefined) return undefined
    result[key] = memory
  }
  return result
}
